import MP4Box from 'mp4box';
import { MB } from '../../constants';

export const MDAT =
  ('m'.charCodeAt(0) << 24) | ('d'.charCodeAt(0) << 16) | ('a'.charCodeAt(0) << 8) | 't'.charCodeAt(0);
export const MOOV =
  ('m'.charCodeAt(0) << 24) | ('o'.charCodeAt(0) << 16) | ('o'.charCodeAt(0) << 8) | 'v'.charCodeAt(0);
export const FTYP =
  ('f'.charCodeAt(0) << 24) | ('t'.charCodeAt(0) << 16) | ('y'.charCodeAt(0) << 8) | 'p'.charCodeAt(0);

function _readUint64(dataView: DataView, offset: number, _littleEndian: boolean): number {
  // This can't represent all 64 bit numbers, but 9007199254740991 bytes (~8192 TiB) ought to be enough for anybody.
  // From https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DataView#64-bit_integer_values
  const left = dataView.getUint32(offset, false);
  const right = dataView.getUint32(offset + 4, false);
  // not currently used;
  // return littleEndian ? left + 2 ** 32 * right : 2 ** 32 * left + right;
  return 2 ** 32 * left + right;
}

function _readAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }
      reject(new Error('result must be an ArrayBuffer'));
    });
    reader.addEventListener('error', () => reject(reader.error));
    // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsArrayBuffer
    reader.readAsArrayBuffer(blob);
  });
}

async function spliceMp4({
  isCanceled,
  onProgressChange,
  videoFile,
}: {
  isCanceled: () => boolean;
  onProgressChange: (progressBytes: number) => void;
  videoFile: File;
}): Promise<MP4Box.MP4File | null> {
  // Immediately after a file is selected we will begin analyzing it.
  const newMp4BoxFile = MP4Box.createFile();
  /*
  Does not trigger so commented out for now, 
  all errors currently being caught in catch block
  newMp4BoxFile.onError = (e) => {}
  */
  const bufferSize = 32 * MB;

  let data = new DataView(new ArrayBuffer(0));
  let fileIndex = 0; // Where we are in the file
  let bufferIndex = 0; // Where we are in the buffer
  let processedSize = 0; // How many bytes have been read excluding mdat boxes
  let isFirstBox = true;

  while (fileIndex < videoFile.size) {
    if (isCanceled()) {
      return null;
    }
    // We may read up to 16 bytes of data for the size, box type and optional 64 bit size
    if (bufferIndex + 15 >= data.byteLength) {
      bufferIndex = 0;
      data = new DataView(await _readAsArrayBuffer(videoFile.slice(fileIndex, fileIndex + bufferSize)));

      await onProgressChange(fileIndex);
    }
    const boxSize = (() => {
      const size = data.getUint32(bufferIndex, false);
      if (size === 0) {
        // The box takes up the remainder of the file
        return videoFile.size - fileIndex;
      }
      if (size === 1) {
        // The box has a 64 bit size
        return _readUint64(data, bufferIndex + 8, false);
      }
      return size;
    })();
    const boxType = data.getUint32(bufferIndex + 4, false);

    if (isFirstBox && boxType !== FTYP) {
      throw new Error('failed to find ftyp box at the start of the file; likely not a valid MP4');
    }
    isFirstBox = false;

    if (boxType !== MDAT) {
      const boxBuffer =
        bufferIndex + boxSize >= data.byteLength
          ? await _readAsArrayBuffer(videoFile.slice(fileIndex, fileIndex + boxSize))
          : data.buffer.slice(bufferIndex, bufferIndex + boxSize);

      Object.defineProperty(boxBuffer, 'fileStart', { value: processedSize });

      newMp4BoxFile.appendBuffer(boxBuffer);

      processedSize += boxSize;
    }
    fileIndex += boxSize;
    bufferIndex += boxSize;
  }

  return newMp4BoxFile;
}

export default spliceMp4;
